import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Download } from 'lucide-react';

// Discord SVG Icon Component
const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [docsDropdownOpen, setDocsDropdownOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const productLinks = [
    { name: 'Install Twe', href: '/installation', description: 'Setup guide & download' },
    { name: 'Voice Control', href: '/recipes', description: 'Natural voice commands' },
    // { name: 'Browser Automation', href: '/docs', description: 'Playwright-powered' },
    { name: 'Safety Architecture', href: '/safety', description: 'Trust & safety' }
  ];

  const docsLinks = [
    // { name: 'Documentation', href: '/docs', description: 'Technical guides & API' },
    { name: 'Changelog', href: '/changelog', description: 'Version history' },
    { name: 'Blog', href: '/blog', description: 'News & articles' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/5">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="/logo.png" 
              alt="Twe Logo" 
              className="h-10 w-10 transition-transform duration-300 group-hover:scale-110"
            />
            <span className="text-xl font-bold text-white">Twe</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {/* Product Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setProductDropdownOpen(true)}
              onMouseLeave={() => setProductDropdownOpen(false)}
            >
              <button className="flex items-center space-x-1 px-4 py-2 text-gray-300 hover:text-white transition-colors duration-200">
                <span>Product</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${productDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {productDropdownOpen && (
                <div className="absolute top-full left-0 pt-0 w-64">
                  <div className="h-2"></div>
                  <div className="bg-zinc-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden">
                    {productLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.href}
                        onClick={() => setProductDropdownOpen(false)}
                        className="block px-4 py-3 hover:bg-white/10 transition-colors duration-200 border-b border-white/5 last:border-0"
                      >
                        <div className="text-white font-medium">{link.name}</div>
                        <div className="text-sm text-gray-400 mt-0.5">{link.description}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/recipes"
              className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                isActive('/recipes') ? 'text-orange-500' : 'text-gray-300 hover:text-white'
              }`}
            >
              Recipes
            </Link>

            {/* Docs Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setDocsDropdownOpen(true)}
              onMouseLeave={() => setDocsDropdownOpen(false)}
            >
              <button className={`flex items-center space-x-1 px-4 py-2 transition-colors duration-200 ${
                isActive('/docs') || isActive('/changelog') || isActive('/blog') ? 'text-orange-500' : 'text-gray-300 hover:text-white'
              }`}>
                <span>Docs</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${docsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {docsDropdownOpen && (
                <div className="absolute top-full left-0 pt-0 w-64">
                  <div className="h-2"></div>
                  <div className="bg-zinc-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden">
                    {docsLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.href}
                        onClick={() => setDocsDropdownOpen(false)}
                        className="block px-4 py-3 hover:bg-white/10 transition-colors duration-200 border-b border-white/5 last:border-0"
                      >
                        <div className="text-white font-medium">{link.name}</div>
                        <div className="text-sm text-gray-400 mt-0.5">{link.description}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/roadmap"
              className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                isActive('/roadmap') ? 'text-orange-500' : 'text-gray-300 hover:text-white'
              }`}
            >
              Roadmap
            </Link>
            <Link
              to="/pricing"
              className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                isActive('/pricing') ? 'text-orange-500' : 'text-gray-300 hover:text-white'
              }`}
            >
              Pricing
            </Link>
          </div>

          {/* CTA Buttons - Shortened */}
          <div className="hidden md:flex items-center space-x-2">
            <a
              href="https://discord.gg/AUezcNccMd"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-1.5 px-3 py-2 bg-zinc-800 text-white text-sm font-medium rounded-lg hover:bg-zinc-700 transition-all duration-300 border border-white/10"
            >
              <DiscordIcon className="w-4 h-4" />
              <span>Discord</span>
            </a>
            <a
              href="https://downloads.twe-assistant.com/latest/twe-voice-assistant-setup.exe"
              download
              className="flex items-center space-x-1.5 px-3 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white text-sm font-medium rounded-lg hover:shadow-lg hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105"
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-300 hover:text-white transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/5">
            <div className="flex flex-col space-y-2">
              <Link
                to="/recipes"
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Recipes
              </Link>
              <Link
                to="/safety"
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Safety Architecture
              </Link>
              {/* <Link
                to="/docs"
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Docs
              </Link> */}
              <Link
                to="/changelog"
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors pl-8"
                onClick={() => setMobileMenuOpen(false)}
              >
                Changelog
              </Link>
              <Link
                to="/blog"
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors pl-8"
                onClick={() => setMobileMenuOpen(false)}
              >
                Blog
              </Link>
              <Link
                to="/roadmap"
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Roadmap
              </Link>
              <Link
                to="/pricing"
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              <div className="flex flex-col space-y-2 mx-4 mt-4">
                <a
                  href="https://discord.gg/AUezcNccMd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-zinc-800 text-white font-medium rounded-lg border border-white/10"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <DiscordIcon className="w-4 h-4" />
                  <span>Discord</span>
                </a>
                <a
                  href="https://downloads.twe-assistant.com/latest/twe-voice-assistant-setup.exe"
                  download
                  className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 text-white font-medium rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
